import {connect} from "redux-vue";
import Validator from "validatorjs";
import * as userActions from "../actions/user";
import ModalTemplate, {modalPageDecorator} from "./ModalTemplate";
import ValidatedComponent from "../components/ValidatedComponent";
import PasswordInput from "../components/PasswordInput";
import LoadingIndicator from "../components/LoadingIndicator";

const RegisterModal = modalPageDecorator({
  props: ['app', 'user', 'login', 'register', 'uploadPhoto'],

  data() {
    return {
      formData: {
        first_name: "",
        last_name: "",
        email: "",
        email_confirmation: "",
        country: "",
        role: "",
        role_name: "",
        password: "",
        password_confirmation: ""
      },
      formRules: {
        first_name: "required|max:30",
        last_name: "required|max:30",
        email: "required|email",
        email_confirmation: "required|same:email",
        country: "required",
        role: "required",
        role_name: "",
        password: "required|min:8",
        password_confirmation: "required|same:password"
      },
      formErrors: null,
      fileData: null,
      formLabels: {
        first_name: this.$t('register_modal.first_name_label'),
        last_name: this.$t('register_modal.last_name_label'),
        email: this.$t('register_modal.email_label'),
        email_confirmation: this.$t('register_modal.email_confirmation_label'),
        country: this.$t('register_modal.country_label'),
        role: this.$t('register_modal.role_label'),
        role_name: this.$t('register_modal.role_name_label'),
        password: this.$t('register_modal.password_label'),
        password_confirmation: this.$t('register_modal.password_confirmation_label')
      },
    }
  },

  computed: {
    roleNameVisible() {
      const otherRole = this.app.roles.find(
        i => i.role.toLowerCase() === "other"
      );
      return otherRole && otherRole.id === this.formData.role;
    }
  },

  methods: {
    /**
     *
     */
    onAvatarEditClick () {
      this.$refs.aviInput && this.$refs.aviInput.click();
    },

    /**
     *
     * @param event
     */
    uploadProfileAvatar (event) {
      if (event.target.files.length && !this.user.photoUpload) {
        const file = event.target.files[0];
        this.fileData = new FormData();
        this.fileData.append("photo", file);
        this.$refs.aviFilename.textContent = file.name;
      }
    },

    /**
     *
     * @param event
     * @returns {Promise<void>}
     */
    async handleSubmit (event) {
      event.preventDefault();

      if (this.roleNameVisible) {
        this.formRules.role_name = "required";
      } else {
        this.formRules.role_name = "";
      }

      const validation = new Validator(this.formData, this.formRules);

      validation.setAttributeFormatter((attribute) =>
        this.formLabels[attribute].toLowerCase()
      );

      if (validation.passes()) {
        this.formErrors = null;

        await this.register(this.formData);

        if (this.user.registerSuccess) {
          await this.login(this.formData.email, this.formData.password);

          if (this.user.loginSuccess) {
            if (this.fileData) {
              this.uploadPhoto(this.fileData);
            }

            if (this.$route.name !== "CoursesPage") {
              this.$router.replace({ name: "CoursesPage" });
            }
          }
        }

        if (this.user.registerError) {
          this.formErrors = this.user.registerError.response.data.errors;
        }
      } else {
        this.formErrors = validation.errors.all();
      }

    },
  },

  /**
   *
   * @param h
   * @returns {*}
   */
  render(h) {
    const inputClassName = "bg-gray-100 text-sm appearance-none border rounded w-full p-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline";
    // "block appearance-none w-full bg-gray-100 text-sm border text-gray-700 p-2 rounded leading-tight focus:outline-none focus:bg-white focus:shadow-outline"
    return (
      <ModalTemplate>
        <div class="m-2 lg:my-12">
          <div class="bg-white container lg:px-12 md:max-w-4xl mx-auto rounded shadow-lg relative z-50">

            <div
              class="absolute top-0 right-0 cursor-pointer flex flex-col items-center mt-4 mr-4 text-white text-sm z-50">
            </div>

            <div class="p-5 lg:pt-5 pb-8 lg:pb-10">
              <div onClick={this.dismiss} class="cursor-pointer z-50 absolute right-0 top-0 m-4">
                <i class="mdi mdi-close text-3xl lg:text-4xl leading-none"></i>
              </div>

              <h3 class="lg:text-xl font-bold mb-3 lg:mt-6">
                Register for an Immunization Academy Account
              </h3>
              <p class="font-bold text-xs mb-3 lg:mb-5">
                This is how your name will appear on any certificates you receive, so please enter your name carefully.
              </p>
              <p class="font-bold text-xs">
                Immunization Academy user?
                <router-link to={{name: "LoginModal"}} class="text-teal-500 font-normal ml-1">Sign in here.</router-link>
              </p>

              <form onSubmit={this.handleSubmit}>
                <div class="flex flex-wrap mt-8 lg:-mx-10">
                  <div class="w-full lg:w-6/12 lg:px-10 mb-16 lg:mb-0">
                    <p class="lg:text-lg font-bold mb-4">
                      Basic Information <span class="text-xs italic font-normal">- All fields required</span>
                    </p>

                    <div class="mb-4">
                      <label class="block text-gray-700 text-sm mb-2" for="first_name">
                        First Name
                      </label>
                      <ValidatedComponent
                        name="first_name"
                        errors={this.formErrors}
                        classname={inputClassName}
                        renderComponent={cn => (
                          <input class={cn} id="first_name" type="text" v-model={this.formData.first_name}/>
                        )}
                      />
                    </div>

                    <div class="mb-4">
                      <label class="block text-gray-700 text-sm mb-2" for="last_name">
                        Surname
                      </label>
                      <ValidatedComponent
                        name="last_name"
                        errors={this.formErrors}
                        classname={inputClassName}
                        renderComponent={cn => (
                          <input class={cn} id="last_name" type="text" v-model={this.formData.last_name}/>
                        )}
                      />
                    </div>

                    <div class="mb-4">
                      <label class="block text-gray-700 text-sm mb-2" for="country">
                        Country
                      </label>
                      <ValidatedComponent
                        name="country"
                        errors={this.formErrors}
                        classname={inputClassName}
                        renderComponent={cn => (
                          <div class="relative">
                            <select class={cn} id="country" v-model={this.formData.country}>
                              <option selected disabled value=""></option>
                              {this.app.countries.map(i => (
                                <option value={i.id} key={i.id}>
                                  {i.country}
                                </option>
                              ))}
                            </select>
                            <div
                              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                              <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                              </svg>
                            </div>
                          </div>
                        )}
                      />
                    </div>

                    <div class="mb-4">
                      <label class="block text-gray-700 text-sm mb-2" for="role">
                        Role
                      </label>
                      <ValidatedComponent
                        name="role"
                        errors={this.formErrors}
                        classname={inputClassName}
                        renderComponent={cn => (
                          <div class="relative">
                            <select class={cn} id="role" v-model={this.formData.role}>
                              <option selected disabled value=""></option>
                              {this.app.roles.map(i => (
                                <option value={i.id} key={i.id}>
                                  {i.role}
                                </option>
                              ))}
                            </select>
                            <div
                              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                              <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg"
                                   viewBox="0 0 20 20">
                                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                              </svg>
                            </div>
                          </div>
                        )}
                      />
                    </div>

                    {this.roleNameVisible && (
                      <div class="mb-4">
                        <label class="block text-gray-700 text-sm mb-2" for="role_name">
                          Role Name
                        </label>
                        <ValidatedComponent
                          name="role_name"
                          errors={this.formErrors}
                          classname={inputClassName}
                          renderComponent={cn => (
                            <input class={cn} id="role_name" type="text" v-model={this.formData.role_name}/>
                          )}
                        />
                      </div>
                    )}

                    <div class="mb-4">
                      <label class="block text-gray-700 text-sm mb-2" for="password">
                        Password
                      </label>
                      <ValidatedComponent
                        name="password"
                        errors={this.formErrors}
                        classname={inputClassName}
                        renderComponent={cn => (
                          <PasswordInput v-model={this.formData.password} classname={cn}/>
                        )}
                      />
                    </div>

                    <div class="mb-4">
                      <label class="block text-gray-700 text-sm mb-2" for="password_confirmation">
                        Password Confirmation
                      </label>
                      <ValidatedComponent
                        name="password_confirmation"
                        errors={this.formErrors}
                        classname={inputClassName}
                        renderComponent={cn => (
                          <PasswordInput id="password_confirmation" v-model={this.formData.password_confirmation} classname={cn}/>
                        )}
                      />
                    </div>
                  </div>

                  <div class="w-full lg:w-6/12 lg:px-10">
                    <p class="lg:text-lg font-bold mb-4">
                      Contact Information <span class="text-xs italic font-normal">- All fields required</span>
                    </p>

                    <div class="mb-4">
                      <label class="block text-gray-700 text-sm mb-2" for="email">
                        Email Address
                      </label>
                      <ValidatedComponent
                        name="email"
                        errors={this.formErrors}
                        classname={inputClassName}
                        renderComponent={cn => (
                          <input class={cn} id="email" type="email" v-model={this.formData.email}/>
                        )}
                      />
                    </div>

                    <div class="mb-12">
                      <label class="block text-gray-700 text-sm mb-2" for="confirm-email">
                        Confirm Email Address
                      </label>
                      <ValidatedComponent
                        name="email_confirmation"
                        errors={this.formErrors}
                        classname={inputClassName}
                        renderComponent={cn => (
                          <input class={cn} id="email_confirmation" type="email" v-model={this.formData.email_confirmation}/>
                        )}
                      />
                    </div>

                    <p class="lg:text-lg font-bold mb-4 border-b border-faded-200">
                      Photo <span class="text-xs italic font-normal">- Optional</span>
                    </p>

                    <div class="mb-10">
                      <p class="mt-5 lg:mt-0 mb-1 lg:mb-4 text-xs lg:text-sm" ref="aviFilename">XXXX.jpg</p>
                      <button
                        class="px-3 p-1 rounded-full focus:outline-none focus:shadow-outline text-xs lg:text-sm border-teal-500 border-2"
                        type="button" onClick={this.onAvatarEditClick} disabled={this.user.photoUpload}>
                        {this.user.photoUpload && <LoadingIndicator/>}
                        {this.user.photoUpload || (
                          <fragment>
                            <i class="mdi mdi-upload mr-2"></i>
                            Upload new...
                          </fragment>
                        )}
                      </button>
                      <input type="file" ref="aviInput" accept="image/png,image/gif,image/jpeg"
                             onChange={this.uploadProfileAvatar} class="hidden"/>
                    </div>

                    <div class="flex flex-col lg:flex-row items-start lg:items-center mr-12 lg:-mr-10">
                      <button
                        class="flex-shrink-0 lg:mr-8 mb-3 lg:mb-0 px-5 p-2 lg:p-3 rounded-full font-serif bg-yellow-500 focus:outline-none focus:shadow-outline text-base"
                        type="submit">
                        Register now
                        <i class="mdi mdi-arrow-right ml-1"></i>
                      </button>
                      <p class="italic text-xs">
                        By registering, you are accepting our
                        <a href="#" class="text-teal-500 mx-1">Terms &amp; Conditions</a>
                        and <a href="#" class="text-teal-500 mx-1">Privacy Policy.</a>
                      </p>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </ModalTemplate>
    );
  }
});

function mapStateToProps(state) {
  return {
    app: state.app,
    user: state.user
  };
}

function mapActionToProps(dispatch) {
  return {
    login: async (email, password) => await dispatch(userActions.login(email, password)),
    register: async data => await dispatch(userActions.register(data)),
    uploadPhoto: (data) => dispatch(userActions.uploadPhoto(data)),
  };
}

export default connect(mapStateToProps, mapActionToProps)(RegisterModal);
